import { useState, useEffect } from 'react'
import { PaymentChannel, PaymentChannelFormData } from '@/types'
import '../../pages/parameters/styles/TabStyles.css'

interface PaymentChannelEditModalProps {
  isOpen: boolean
  channel: PaymentChannel | null
  onClose: () => void
  onSave: (id: string, updates: Partial<PaymentChannel>) => Promise<void>
  loading: boolean
}

export default function PaymentChannelEditModal({
  isOpen,
  channel,
  onClose,
  onSave,
  loading
}: PaymentChannelEditModalProps) {
  const [formData, setFormData] = useState<PaymentChannelFormData>({
    channel_name: '',
    channel_icon: '',
    description: '',
    tips: '',
    config: {
      min_amount: 0,
      max_amount: 0,
      fee_rate: 0,
      networks: [],
      default_network: ''
    }
  })
  const [networkInput, setNetworkInput] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (channel) {
      setFormData({
        channel_name: channel.channel_name,
        channel_icon: channel.channel_icon,
        description: channel.description,
        tips: channel.tips,
        config: {
          ...channel.config,
          networks: channel.config.networks || [],
          default_network: channel.config.default_network || ''
        }
      })
      setErrors({})
    }
  }, [channel])

  const showNetworks = channel?.channel_code === 'usdt' || channel?.channel_code === 'okx'

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.channel_name.trim()) {
      newErrors.channel_name = '通道名称不能为空'
    } else if (formData.channel_name.length > 50) {
      newErrors.channel_name = '通道名称不能超过50个字符'
    }

    if (formData.config.min_amount < 0) {
      newErrors.min_amount = '最小金额不能小于0'
    }

    if (formData.config.max_amount < 0) {
      newErrors.max_amount = '最大金额不能小于0'
    }

    if (formData.config.min_amount >= formData.config.max_amount) {
      newErrors.min_amount = '最小金额必须小于最大金额'
    }

    if (formData.config.fee_rate < 0 || formData.config.fee_rate > 1) {
      newErrors.fee_rate = '手续费率必须在0到1之间'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!channel || !validateForm()) {
      return
    }

    await onSave(channel.id, formData)
  }

  const handleAddNetwork = () => {
    if (networkInput.trim() && !formData.config.networks?.includes(networkInput.trim())) {
      setFormData({
        ...formData,
        config: {
          ...formData.config,
          networks: [...(formData.config.networks || []), networkInput.trim()]
        }
      })
      setNetworkInput('')
    }
  }

  const handleRemoveNetwork = (network: string) => {
    setFormData({
      ...formData,
      config: {
        ...formData.config,
        networks: (formData.config.networks || []).filter(n => n !== network)
      }
    })
  }

  if (!isOpen || !channel) return null

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h4>编辑支付通道</h4>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>通道名称 *</label>
              <input
                type="text"
                value={formData.channel_name}
                onChange={e => setFormData({ ...formData, channel_name: e.target.value })}
                placeholder="输入通道名称"
              />
              {errors.channel_name && (
                <small style={{ color: '#f5222d' }}>{errors.channel_name}</small>
              )}
            </div>

            <div className="form-group">
              <label>图标</label>
              <input
                type="text"
                value={formData.channel_icon}
                onChange={e => setFormData({ ...formData, channel_icon: e.target.value })}
                placeholder="输入图标 emoji"
              />
            </div>

            <div className="form-group">
              <label>最小金额 *</label>
              <input
                type="number"
                step="0.01"
                value={formData.config.min_amount}
                onChange={e => setFormData({
                  ...formData,
                  config: { ...formData.config, min_amount: parseFloat(e.target.value) || 0 }
                })}
                placeholder="输入最小金额"
              />
              {errors.min_amount && (
                <small style={{ color: '#f5222d' }}>{errors.min_amount}</small>
              )}
            </div>

            <div className="form-group">
              <label>最大金额 *</label>
              <input
                type="number"
                step="0.01"
                value={formData.config.max_amount}
                onChange={e => setFormData({
                  ...formData,
                  config: { ...formData.config, max_amount: parseFloat(e.target.value) || 0 }
                })}
                placeholder="输入最大金额"
              />
              {errors.max_amount && (
                <small style={{ color: '#f5222d' }}>{errors.max_amount}</small>
              )}
            </div>

            <div className="form-group">
              <label>手续费率 (0-1) *</label>
              <input
                type="number"
                step="0.01"
                value={formData.config.fee_rate}
                onChange={e => setFormData({
                  ...formData,
                  config: { ...formData.config, fee_rate: parseFloat(e.target.value) || 0 }
                })}
                placeholder="输入手续费率"
              />
              {errors.fee_rate && (
                <small style={{ color: '#f5222d' }}>{errors.fee_rate}</small>
              )}
            </div>

            {showNetworks && (
              <div className="form-group">
                <label>支持的网络</label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="text"
                    value={networkInput}
                    onChange={e => setNetworkInput(e.target.value)}
                    placeholder="输入网络名称"
                    onKeyPress={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleAddNetwork()
                      }
                    }}
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="btn-small"
                    onClick={handleAddNetwork}
                  >
                    添加
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {(formData.config.networks || []).map(network => (
                    <span
                      key={network}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        background: '#f0f0f0',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}
                    >
                      {network}
                      <button
                        type="button"
                        onClick={() => handleRemoveNetwork(network)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '0 4px',
                          fontSize: '14px',
                          color: '#999'
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="form-group">
              <label>描述</label>
              <textarea
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                placeholder="输入通道描述"
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d0d0d0',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>

            <div className="form-group">
              <label>使用提示</label>
              <textarea
                value={formData.tips}
                onChange={e => setFormData({ ...formData, tips: e.target.value })}
                placeholder="输入使用提示"
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d0d0d0',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              取消
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? '保存中...' : '保存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
